import { useDataStore } from '@/store/useDataStore';

interface Contact {
  id: string;
  name?: string;
  status?: string;
  avatar_color?: string;
  avatar_url?: string;
  persona_notes?: string | string[];
}

interface GroupMessage {
  last_speaker_contact_id?: string;
  participants?: string[];
}

/**
 * Composable for avatar-related utilities
 */
export function useAvatarUtilities(currentUserId: string) {
  const dataStore = useDataStore();
  const contacts = dataStore.contactsData?.contacts || [];

  /**
   * Find a contact by ID
   */
  function findContactById(contactId: string): Contact | undefined {
    return dataStore.contactsData.contacts.find((c: Contact) => c.id === contactId);
  }

  /**
   * Create avatar props from contact data
   */
  function createAvatarPropsFromContact(contact: Contact | undefined, additionalProps = {}) {
    if (!contact) return null;

    return {
      fullName: contact.name,
      avatarColor: contact.avatar_color,
      imageSrc: contact.avatar_url,
      imageAlt: contact.name,
      id: contact.id,
      size: 'sm',
      ...additionalProps,
    };
  }

  /**
   * Get avatar data for contact type items in feed views (without presence)
   */
  function getContactAvatarData(contactId: string) {
    // Special handling for system messages
    if (contactId === 'System') {
      return {
        props: {
          fullName: 'Spark',
          imageSrc: './images/sparkAvatar@2x.png', // Path matching format in contacts.json
          imageAlt: 'Spark',
          id: 'system',
          size: 'sm',
        },
        isSystem: true,
      };
    }

    const contact = findContactById(contactId);
    if (!contact) return null;

    // Don't include presence for feed view avatars
    const props = createAvatarPropsFromContact(contact);

    return {
      props,
    };
  }

  /**
   * Get avatar data for contact type items in sidebar and participant lists (with presence)
   */
  function getContactAvatarDataWithPresence(contactId: string) {
    // Special handling for system messages
    if (contactId === 'System') {
      return {
        props: {
          fullName: 'Spark',
          imageSrc: './images/sparkAvatar@2x.png', // Path matching format in contacts.json
          imageAlt: 'Spark',
          id: 'system',
          size: 'sm',
        },
        isSystem: true,
      };
    }

    const contact = findContactById(contactId);
    if (!contact) return null;

    // Include presence for sidebar and participant list avatars
    const props = createAvatarPropsFromContact(contact, {
      presence: contact.status,
    });

    return {
      props,
    };
  }

  /**
   * Calculate group participant count
   */
  function calculateParticipantCount(participants?: string[]) {
    if (!participants) return 0;
    return participants.filter(id => id !== currentUserId).length;
  }

  /**
   * Get avatar data for group message type items
   */
  function getGroupMessageAvatarData(group: GroupMessage) {
    if (!group) return null;

    const lastSpeakerId = group.last_speaker_contact_id;
    const contact = contacts.find((c: Contact) => c.id === lastSpeakerId);
    const participantCount = calculateParticipantCount(group.participants);

    const props = createAvatarPropsFromContact(contact, {
      group: participantCount > 1 ? participantCount : null,
    });

    return {
      props,
    };
  }

  /**
   * Get avatar data for the current user
   */
  function getCurrentUserAvatarData(size?: string) {
    const currentUser = findContactById(currentUserId);
    if (!currentUser) return null;

    const props = createAvatarPropsFromContact(currentUser, {
      size: size || 'sm',
    });

    return {
      props,
    };
  }

  /**
   * Create avatar props with presence status
   */
  function createAvatarPropsWithPresence(contact: Contact, presence?: string, size?: string) {
    if (!contact) return null;

    const mappedPresence = mapPresenceStatus(presence || contact.status);

    return createAvatarPropsFromContact(contact, {
      presence: mappedPresence,
      size: size || 'sm',
    });
  }

  /**
   * Map presence status to standardized values
   */
  function mapPresenceStatus(status?: string): string | undefined {
    if (!status) return undefined;

    const statusMap: Record<string, string> = {
      online: 'available',
      away: 'away',
      busy: 'busy',
      dnd: 'busy',
      do_not_disturb: 'busy',
      offline: 'offline',
      invisible: 'offline',
    };

    return statusMap[status.toLowerCase()] || status;
  }

  /**
   * Create avatar CSS classes
   */
  function createAvatarClasses(baseClass?: string, additionalClasses?: string[]): string {
    const classes = [];

    if (baseClass) {
      classes.push(baseClass);
    }

    if (additionalClasses && additionalClasses.length > 0) {
      classes.push(...additionalClasses);
    }

    return classes.join(' ');
  }

  /**
   * Generate a consistent avatar color based on an ID
   * @param id - The ID to generate a color from
   * @returns A color value from the predefined palette
   */
  function generateAvatarColor(id: string): string {
    const colors = ['300', '400', '500', '600', '700', '800', '900', '1000', '1100', '1200'];
    const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }

  /**
   * Check if a message sender is the system
   */
  function isSystemSender(senderId: string): boolean {
    return senderId === 'System';
  }

  return {
    findContactById,
    createAvatarPropsFromContact,
    getContactAvatarData,
    getContactAvatarDataWithPresence,
    calculateParticipantCount,
    getGroupMessageAvatarData,
    getCurrentUserAvatarData,
    createAvatarPropsWithPresence,
    mapPresenceStatus,
    createAvatarClasses,
    generateAvatarColor,
    isSystemSender,
  };
}
